import { useEffect, useRef, useState } from 'react';
import { Layout, Collapse, Form, Button } from 'antd';
import { ChevronUp, ChevronDown } from 'react-feather';
import { useSearchParams } from 'react-router-dom';

import {
  Logo,
  TestEnvPanel,
  TargetSettingsPanel,
  DesignSettingsPanel,
  ContentSkeleton,
  ShoppablePanel,
} from '../../components';

import './style.scss';
import {
  getUserProperties,
  handleInitilazionData,
  handleStoryProductAdded,
  rebuildStorylyDOM,
} from '../../utils';
import StorySkeleton from '../../components/Skeletons/StorySkeleton';
import { storylyInitialValues } from '../../constants';
import json5 from 'json5';

const { Header, Sider, Content } = Layout;

const TestEnv = () => {
  const storylyRef = useRef();
  const storylyContainerRef = useRef();
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userProperties, setUserProperties] = useState([]);
  const [token, setToken] = useState(null);
  const jsVersion = searchParams.get('version') || 'sdk/2.*';
  const initialValues = {
    ...storylyInitialValues,
    version: jsVersion,
    env: searchParams.get('env') || 'prod',
    token: searchParams.get('token'),
  };

  const borderUnseenColor = Form.useWatch(
    ['props', 'storyGroupIconBorderColorNotSeen'],
    { form, preserve: true }
  );
  const borderSeenColor = Form.useWatch(
    ['props', 'storyGroupIconBorderColorSeen'],
    { form, preserve: true }
  );

  useEffect(() => {
    storylyRef.current?.setProperties({
      storyGroupIconBorderColorNotSeen: borderUnseenColor,
    });
  }, [borderUnseenColor]);

  useEffect(() => {
    storylyRef.current?.setProperties({
      storyGroupIconBorderColorSeen: borderSeenColor,
    });
  }, [borderSeenColor]);

  useEffect(() => {
    if (!document.querySelector('#storyly-web-script')) {
      const storylyScript = document.createElement('script');
      storylyScript.setAttribute('custom-element', 'storyly-web');
      storylyScript.setAttribute('id', 'storyly-web-script');
      storylyScript.setAttribute(
        'src',
        `https://web-story.storyly.io/${jsVersion}/storyly-web.js`
      );
      document.body.appendChild(storylyScript);
    }
  }, []);

  const onFormSubmit = (data) => {
    searchParams.set('env', data.env);
    setSearchParams(searchParams);

    if (data.token) {
      setToken(data.token);
      rebuildStorylyDOM(storylyRef, storylyContainerRef);

      storylyRef.current.init({
        ...handleInitilazionData({
          ...initialValues,
          ...data,
          cssEnv: searchParams.get('version') === 'e2e' ? 'e2e' : undefined,
        }),
      });

      storylyRef.current.on('loadded', (d) => {
        setUserProperties(getUserProperties(d));
        if (data.productHydrationInput) {
          storylyRef.current.setStorylyHydration(
            json5.parse(data.productHydrationInput)
          );
        }
      });

      storylyRef.current.on('storyCartUpdate', handleStoryProductAdded);
      storylyRef.current.on('storylyHydration', (products) => {
        form.setFieldValue('productIds', JSON.stringify(products));
      });

      storylyRef.current.on('storyWishlistUpdate', (data) => {
        const { product, group, type, onError, onSuccess } = data;
        onSuccess({});
      });
    } else {
      setToken('');
      storylyRef.current.hidden = true;
    }
  };

  const onFormChange = (data) => {
    const formValues = form.getFieldsValue();

    if (data.props) {
      const mergedData = { ...formValues.props, ...data.props };

      const {
        storyGroupSizeWidth,
        storyGroupSizeHeight,
        storyGroupBorderSize,
        storyGroupAnimation,
        scrollIndicator,
      } = mergedData;

      storylyRef.current.setProperties({
        ...mergedData,
        scrollIndicator: scrollIndicator === 'on',
        storyGroupSize: `${storyGroupSizeWidth}x${storyGroupSizeHeight}`,
        storyGroupBorderSize: +storyGroupBorderSize,
        storyGroupAnimation: storyGroupAnimation ? { type: 'pulse' } : false,
      });
    }
  };

  const rightMenu = [
    {
      key: '1',
      label: 'Testing Environment',
      children: <TestEnvPanel />,
      forceRender: true,
    },
    {
      key: '2',
      label: 'Target Settings',
      children: (
        <TargetSettingsPanel form={form} userProperties={userProperties} />
      ),
      forceRender: true,
    },
    {
      key: '3',
      label: 'Design Settings',
      children: <DesignSettingsPanel />,
      forceRender: true,
    },
    {
      key: '4',
      label: 'Shoppable Settings',
      children: <ShoppablePanel storylyRef={storylyRef} />,
      forceRender: true,
    },
  ];

  return (
    <Layout className="test-env-wrapper">
      <Header className="test-env-wrapper-header">
        <Logo />
        <h2>Test Environment</h2>
      </Header>
      <Layout
        className="test-env-layout"
        style={{
          height: 'calc(100vh - 64px)',
        }}
      >
        <Content>
          <div
            style={{
              width: form.getFieldsValue().props?.storyPreviewWidth
                ? `${form.getFieldsValue().props?.storyPreviewWidth}px`
                : '100%',
            }}
            ref={storylyContainerRef}
          >
            {!token && <StorySkeleton />}
          </div>
          <ContentSkeleton />
        </Content>
        <Sider width={369} className="test-env-wrapper-sider">
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onFormSubmit}
            onValuesChange={onFormChange}
            initialValues={initialValues}
          >
            <Collapse
              accordion
              defaultActiveKey="1"
              ghost
              expandIconPosition="end"
              expandIcon={({ isActive }) => {
                return isActive ? <ChevronUp /> : <ChevronDown />;
              }}
              items={rightMenu}
            />
            <div className="test-env-wrapper-buttons">
              <Form.Item noStyle>
                <Button
                  size="large"
                  htmlType="button"
                  onClick={() => {
                    setToken('');
                    onFormSubmit(initialValues);
                    form.setFieldsValue({ ...initialValues, token: '' });
                  }}
                >
                  Reset Settings
                </Button>
                <Button
                  type="primary"
                  data-cy="apply"
                  size="large"
                  htmlType="submit"
                >
                  Apply Changes
                </Button>
              </Form.Item>
            </div>
          </Form>
        </Sider>
      </Layout>
    </Layout>
  );
};

export default TestEnv;
